import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Lock, Pencil, Plus } from "lucide-react";
import type { Id } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { Fab } from "@/components/shell/Fab";
import { LoadFailed, NoMatches } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { renderCell } from "@/components/table/cells";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { DataTable } from "@/components/table/DataTable";
import { Pagination } from "@/components/table/Pagination";
import { useCursorHistory } from "@/components/table/useCursorHistory";
import { CreateTransferForm } from "@/modules/warehouses/screens/CreateTransferForm";
import { TransferDetail } from "@/modules/warehouses/screens/TransferDetail";
import {
  projectTransfer,
  type TransferRow,
} from "@/modules/warehouses/projections";
import type { SectionScreenProps } from "@/modules/types";

/**
 * The transfers list, plus a master-detail panel for create, dispatch,
 * receive and cancel (M6-14).
 *
 * `draft` is the only status still editable — everything from `dispatched`
 * onward has already moved stock at the channel, which is what the pencil
 * versus lock glyph next to the status badge signals. Selecting a row opens
 * it in the detail panel the same way `WarehouseList` opens a warehouse;
 * "New transfer" swaps that panel to the create form instead, rather than
 * opening a second surface, so there is only ever one write panel on screen.
 */
export function TransfersList({
  fields,
  filter,
  sort,
  onToggleSort,
  onClearFilter,
}: SectionScreenProps) {
  const data = useDataSource();

  const [limit, setLimit] = useState(25);
  const [history, setHistory] = useCursorHistory();
  const cursor = history[history.length - 1];
  const [selectedId, setSelectedId] = useState<Id | null>(null);
  const [creating, setCreating] = useState(false);

  /**
   * A second, small query rather than a join `WarehousesRepository` does not
   * offer: `Transfer` only carries `fromWarehouseId` / `toWarehouseId`, and
   * with two warehouses total, fetching all of them once is cheaper than
   * teaching the repository a join no other caller needs.
   */
  const warehousesQuery = useQuery({
    queryKey: ["warehouses", "list-all"],
    queryFn: () => data.warehouses.list({ limit: 200 }),
  });

  const transfersQuery = useQuery({
    queryKey: ["warehouses", "transfers", filter, sort, limit, cursor],
    queryFn: () =>
      data.warehouses.listTransfers({ filter, sort, limit, cursor }),
  });

  if (warehousesQuery.isPending || transfersQuery.isPending) {
    return <TableSkeleton />;
  }

  if (warehousesQuery.isError) {
    return (
      <LoadFailed
        message={warehousesQuery.error.message}
        onRetry={() => {
          void warehousesQuery.refetch();
        }}
      />
    );
  }

  if (transfersQuery.isError) {
    return (
      <LoadFailed
        message={transfersQuery.error.message}
        onRetry={() => {
          void transfersQuery.refetch();
        }}
      />
    );
  }

  const nameById = new Map(
    warehousesQuery.data.items.map((warehouse) => [
      warehouse.id,
      warehouse.name,
    ]),
  );
  const rows: TransferRow[] = transfersQuery.data.items.map((transfer) => ({
    ...transfer,
    fromWarehouseName:
      nameById.get(transfer.fromWarehouseId) ?? transfer.fromWarehouseId,
    toWarehouseName:
      nameById.get(transfer.toWarehouseId) ?? transfer.toWarehouseId,
  }));

  // Looked up against the current page only, the same simplification
  // `WarehouseList` makes for its own selection — the fixture's two
  // warehouses and eight transfers both fit on one page, so a selection
  // that scrolled off elsewhere is not a case this screen has met yet.
  const selected = rows.find((row) => row.id === selectedId) ?? null;

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 lg:flex-row">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="max-md:hidden flex shrink-0 justify-end border-b px-4 py-2">
          <Button
            type="button"
            size="sm"
            onClick={() => {
              setSelectedId(null);
              setCreating(true);
            }}
          >
            <Plus />
            New transfer
          </Button>
        </div>
        <Fab
          label="New transfer"
          icon={<Plus aria-hidden />}
          onClick={() => {
            setSelectedId(null);
            setCreating(true);
          }}
        />

        <DataTable
          fields={fields}
          rows={rows}
          getRowId={(row) => row.id}
          project={projectTransfer}
          sort={sort}
          onToggleSort={onToggleSort}
          onRowClick={(row) => {
            setCreating(false);
            setSelectedId(row.id);
          }}
          renderOverride={(field, row) => {
            if (field.name !== "status") return undefined;
            return (
              <span className="inline-flex items-center gap-1.5">
                {renderCell(field, row.status)}
                {row.status === "draft" ? (
                  <Pencil
                    className="text-muted-foreground size-3"
                    aria-hidden
                  />
                ) : (
                  <Lock className="text-muted-foreground size-3" aria-hidden />
                )}
              </span>
            );
          }}
          emptyState={<NoMatches onClear={onClearFilter} />}
          footer={
            <Pagination
              page={transfersQuery.data}
              limit={limit}
              onLimitChange={(next) => {
                setLimit(next);
                setHistory([undefined]);
              }}
              history={history}
              onNavigate={setHistory}
            />
          }
        />
      </div>

      {creating ? (
        <Card className="w-full shrink-0 lg:w-96">
          <CardContent>
            <CreateTransferForm
              warehouses={warehousesQuery.data.items}
              onCreated={(transferId) => {
                setCreating(false);
                setSelectedId(transferId);
                // Newest-first is the list's default sort — landing back on
                // page one is what makes the transfer just created actually
                // visible in the table behind this panel.
                setHistory([undefined]);
              }}
              onCancel={() => {
                setCreating(false);
              }}
            />
          </CardContent>
        </Card>
      ) : (
        <TransferDetail key={selected?.id ?? "none"} transfer={selected} />
      )}
    </div>
  );
}
